/**
 * RightPanel — Contextualised right column inside MainLayout.
 *
 * Renders a stack of cards (upcoming items, top opportunities, recent activity,
 * KPIs, etc.) tailored to the active persona. Collapsible: when collapsed,
 * shows a slim rail with stack icons; expanded gives a 320px column.
 */

import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  ChevronRight, ChevronLeft, type LucideIcon,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'

export interface RightPanelListItem {
  id: string
  primary: string
  secondary?: string
  meta?: string
  href?: string
}

export interface RightPanelSliderItem {
  id: string
  title: string
  subtitle?: string
  imageUrl?: string
  badge?: string
  href?: string
}

export type RightPanelCard =
  | {
      kind: 'list'
      id: string
      title: string
      icon?: LucideIcon
      accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
      items: RightPanelListItem[]
      seeAllHref?: string
    }
  | {
      kind: 'kpi'
      id: string
      title: string
      icon?: LucideIcon
      accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
      value: string | number
      delta?: { value: string; positive?: boolean }
      hint?: string
      href?: string
    }
  | {
      kind: 'slider'
      id: string
      title: string
      icon?: LucideIcon
      accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
      items: RightPanelSliderItem[]
      seeAllHref?: string
    }
  | {
      kind: 'custom'
      id: string
      title: string
      icon?: LucideIcon
      accent?: 'blue' | 'amber' | 'green' | 'purple' | 'red' | 'teal' | 'indigo' | 'slate'
      content: React.ReactNode
    }

interface RightPanelProps {
  cards: RightPanelCard[]
  collapsed?: boolean
  onToggle?: () => void
  title?: string
}

const ACCENT_TINT: Record<NonNullable<RightPanelCard['accent']>, string> = {
  blue: 'text-blue-500 bg-blue-500/10',
  amber: 'text-amber-500 bg-amber-500/10',
  green: 'text-green-500 bg-green-500/10',
  purple: 'text-purple-500 bg-purple-500/10',
  red: 'text-red-500 bg-red-500/10',
  teal: 'text-teal-500 bg-teal-500/10',
  indigo: 'text-indigo-500 bg-indigo-500/10',
  slate: 'text-slate-500 bg-slate-500/10',
}

export function RightPanel({
  cards,
  collapsed = false,
  onToggle,
  title,
}: RightPanelProps) {
  const { t } = useTranslation()
  const headerTitle = title ?? t('rightPanel.forYou')
  const [internalCollapsed, setInternalCollapsed] = useState(collapsed)
  const isCollapsed = onToggle ? collapsed : internalCollapsed
  const toggle = onToggle ?? (() => setInternalCollapsed((c) => !c))

  if (isCollapsed) {
    return (
      <aside
        aria-label="Right panel"
        className="shrink-0 w-12 flex flex-col items-center gap-1 border-l border-[hsl(var(--rightpanel-border))] bg-[hsl(var(--rightpanel-bg))] text-[hsl(var(--rightpanel-fg))] py-2"
      >
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={toggle}
          aria-label="Expand right panel"
          title={t('rightPanel.openPanel')}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        {cards.slice(0, 6).map((card) => {
          const Icon = card.icon
          const tint = ACCENT_TINT[card.accent ?? 'slate']
          return (
            <button
              key={card.id}
              type="button"
              onClick={toggle}
              className={cn(
                'flex h-9 w-9 items-center justify-center rounded-lg',
                'transition-colors hover:bg-accent',
                tint,
              )}
              title={card.title}
              aria-label={card.title}
            >
              {Icon && <Icon className="h-4 w-4" />}
            </button>
          )
        })}
      </aside>
    )
  }

  return (
    <aside
      aria-label="Right panel"
      className="shrink-0 w-[320px] flex flex-col border-l border-[hsl(var(--rightpanel-border))] bg-[hsl(var(--rightpanel-bg))] text-[hsl(var(--rightpanel-fg))] min-h-0"
    >
      <header className="flex h-10 items-center justify-between px-3 border-b border-[hsl(var(--rightpanel-border))] shrink-0">
        <h2 className="text-[11px] font-bold uppercase tracking-wider text-[hsl(var(--title-fg))]">
          {headerTitle}
        </h2>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          onClick={toggle}
          aria-label="Collapse right panel"
          title={t('rightPanel.collapsePanel')}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </header>

      <ScrollArea className="flex-1 min-h-0">
        <div className="flex flex-col gap-2 p-3">
          {cards.map((card) => (
            <RightPanelCardView key={card.id} card={card} />
          ))}
          {cards.length === 0 && (
            <p className="text-[12px] text-muted-foreground px-1 py-4 text-center">
              Aucun élément contextuel pour le moment.
            </p>
          )}
        </div>
      </ScrollArea>
    </aside>
  )
}

function RightPanelCardView({ card }: { card: RightPanelCard }) {
  const Icon = card.icon
  const tint = ACCENT_TINT[card.accent ?? 'slate']

  return (
    <section className="rounded-xl border bg-card overflow-hidden">
      <header className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
        {Icon && (
          <span className={cn('flex h-6 w-6 items-center justify-center rounded-md shrink-0', tint)}>
            <Icon className="h-3 w-3" />
          </span>
        )}
        <h3 className="text-[11px] font-bold uppercase tracking-wider text-foreground flex-1 truncate">
          {card.title}
        </h3>
      </header>

      <div className="p-2">
        {card.kind === 'list' && <ListCard card={card} />}
        {card.kind === 'kpi' && <KpiCard card={card} />}
        {card.kind === 'slider' && <SliderCard card={card} />}
        {card.kind === 'custom' && card.content}
      </div>
    </section>
  )
}

function SliderCard({
  card,
}: {
  card: Extract<RightPanelCard, { kind: 'slider' }>
}) {
  const { t } = useTranslation()
  const ref = useRef<HTMLUListElement>(null)
  const scrollBy = (delta: number) => {
    ref.current?.scrollBy({ left: delta, behavior: 'smooth' })
  }
  if (card.items.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground px-1 py-2">
        {t('common.noData')}
      </p>
    )
  }
  return (
    <div className="relative">
      <ul
        ref={ref}
        className="flex gap-2 overflow-x-auto scrollbar-none snap-x snap-mandatory"
      >
        {card.items.map((item) => {
          const inner = (
            <div className="snap-start w-[180px] shrink-0 rounded-lg border bg-card overflow-hidden hover:border-primary/40 transition-colors">
              {item.imageUrl ? (
                <div
                  className="h-20 bg-cover bg-center bg-muted"
                  style={{ backgroundImage: `url(${item.imageUrl})` }}
                />
              ) : (
                <div className="h-20 bg-gradient-to-br from-primary/10 to-blue-400/10" />
              )}
              <div className="p-2 space-y-0.5">
                <div className="flex items-center gap-1.5">
                  <p className="text-[11px] font-semibold text-foreground truncate flex-1">{item.title}</p>
                  {item.badge && (
                    <Badge variant="secondary" className="text-[8px] h-3.5 px-1">{item.badge}</Badge>
                  )}
                </div>
                {item.subtitle && (
                  <p className="text-[10px] text-muted-foreground truncate">{item.subtitle}</p>
                )}
              </div>
            </div>
          )
          return (
            <li key={item.id}>
              {item.href ? <Link to={item.href}>{inner}</Link> : inner}
            </li>
          )
        })}
      </ul>
      {card.items.length > 1 && (
        <>
          <button
            type="button"
            onClick={() => scrollBy(-180)}
            className="absolute left-0 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-card border shadow-sm flex items-center justify-center hover:bg-accent"
            aria-label="Previous"
          >
            <ChevronLeft className="h-3 w-3" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(180)}
            className="absolute right-0 top-1/2 -translate-y-1/2 h-6 w-6 rounded-full bg-card border shadow-sm flex items-center justify-center hover:bg-accent"
            aria-label="Next"
          >
            <ChevronRight className="h-3 w-3" />
          </button>
        </>
      )}
      {card.seeAllHref && (
        <Link
          to={card.seeAllHref}
          className="block text-center text-[11px] font-semibold text-primary hover:underline px-2 py-1.5 mt-1 rounded-md hover:bg-accent"
        >
          {t('rightPanel.seeAll')} →
        </Link>
      )}
    </div>
  )
}

function ListCard({
  card,
}: {
  card: Extract<RightPanelCard, { kind: 'list' }>
}) {
  const { t } = useTranslation()
  if (card.items.length === 0) {
    return (
      <p className="text-[11px] text-muted-foreground px-1 py-2">
        {t('common.noData')}
      </p>
    )
  }
  return (
    <>
      <ul className="flex flex-col">
        {card.items.map((item) => {
          const inner = (
            <>
              <span className="flex-1 min-w-0">
                <span className="block text-[12px] font-medium text-foreground truncate">
                  {item.primary}
                </span>
                {item.secondary && (
                  <span className="block text-[10px] text-muted-foreground truncate">
                    {item.secondary}
                  </span>
                )}
              </span>
              {item.meta && (
                <Badge variant="secondary" className="text-[9px] h-4 px-1.5 shrink-0">
                  {item.meta}
                </Badge>
              )}
            </>
          )
          return (
            <li key={item.id}>
              {item.href ? (
                <Link
                  to={item.href}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-accent transition-colors"
                >
                  {inner}
                </Link>
              ) : (
                <div className="flex items-center gap-2 px-2 py-1.5">{inner}</div>
              )}
            </li>
          )
        })}
      </ul>
      {card.seeAllHref && (
        <Link
          to={card.seeAllHref}
          className="block text-center text-[11px] font-semibold text-primary hover:underline px-2 py-1.5 mt-1 rounded-md hover:bg-accent"
        >
          {t('rightPanel.seeAll')} →
        </Link>
      )}
    </>
  )
}

function KpiCard({
  card,
}: {
  card: Extract<RightPanelCard, { kind: 'kpi' }>
}) {
  const body = (
    <>
      <span className="text-[22px] font-bold tabular-nums text-foreground leading-tight">
        {card.value}
      </span>
      {card.delta && (
        <span
          className={cn(
            'inline-block ml-1 text-[10px] font-semibold',
            card.delta.positive ? 'text-green-600' : 'text-red-600',
          )}
        >
          {card.delta.positive ? '▲' : '▼'} {card.delta.value}
        </span>
      )}
      {card.hint && (
        <span className="block text-[10px] text-muted-foreground mt-0.5">
          {card.hint}
        </span>
      )}
    </>
  )
  return card.href ? (
    <Link to={card.href} className="block px-1 py-1 hover:bg-accent rounded-md transition-colors">
      {body}
    </Link>
  ) : (
    <div className="px-1 py-1">{body}</div>
  )
}
